import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Badge,
  Input,
  Modal,
  Toggle,
  useToast,
  type Column,
} from '@nexora/design-system';
import {
  useTaxProfiles,
  useCreateTaxProfile,
  useUpdateTaxProfile,
  useDeleteTaxProfile,
  type TaxProfile,
  type TaxProfileInput,
} from './useTaxProfiles';

/* ── Constants ── */

const CLASSIFICACAO_OPTIONS = [
  { value: 'Simples Nacional', label: 'Simples Nacional' },
  { value: 'Lucro Presumido', label: 'Lucro Presumido' },
  { value: 'Lucro Real', label: 'Lucro Real' },
];

/* ── Helpers ── */

function emptyProfileInput(): TaxProfileInput {
  return {
    ncmCode: null,
    productId: null,
    classificacaoTributaria: null,
    reducaoIbs: null,
    reducaoCbs: null,
    cstIbsCbs: null,
    monofasico: false,
    diferimento: false,
  };
}

function formatPercent(value: number | null): string {
  return value != null ? `${value}%` : '-';
}

function yesNo(value: boolean): JSX.Element {
  return value
    ? <Badge variant="success">Sim</Badge>
    : <Badge variant="neutral">Não</Badge>;
}

/* ── Component ── */

export function TaxProfilesPage(): JSX.Element {
  const { addToast } = useToast();
  const { data: profiles, isLoading, error, refetch } = useTaxProfiles();
  const createMutation = useCreateTaxProfile();
  const updateMutation = useUpdateTaxProfile();
  const deleteMutation = useDeleteTaxProfile();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TaxProfileInput>(emptyProfileInput());
  const [deleteTarget, setDeleteTarget] = useState<TaxProfile | null>(null);

  useEffect(() => {
    if (!showModal) {
      setEditingId(null);
      setForm(emptyProfileInput());
    }
  }, [showModal]);

  function openCreate(): void {
    setEditingId(null);
    setForm(emptyProfileInput());
    setShowModal(true);
  }

  function openEdit(profile: TaxProfile): void {
    setEditingId(profile.id);
    setForm({
      ncmCode: profile.ncmCode,
      productId: profile.productId,
      classificacaoTributaria: profile.classificacaoTributaria,
      reducaoIbs: profile.reducaoIbs,
      reducaoCbs: profile.reducaoCbs,
      cstIbsCbs: profile.cstIbsCbs,
      monofasico: profile.monofasico,
      diferimento: profile.diferimento,
    });
    setShowModal(true);
  }

  function handleSave(): void {
    if (editingId) {
      updateMutation.mutate({ id: editingId, ...form }, {
        onSuccess: () => {
          addToast('Perfil atualizado com sucesso', 'success');
          setShowModal(false);
        },
        onError: (error) => addToast(error.message, 'error'),
      });
    } else {
      createMutation.mutate(form, {
        onSuccess: () => {
          addToast('Perfil criado com sucesso', 'success');
          setShowModal(false);
        },
        onError: (error) => addToast(error.message, 'error'),
      });
    }
  }

  function handleDelete(profile: TaxProfile): void {
    deleteMutation.mutate(profile.id, {
      onSuccess: () => {
        addToast('Perfil excluido com sucesso', 'success');
        setDeleteTarget(null);
      },
      onError: (error) => addToast(error.message, 'error'),
    });
  }

  const columns: Column<TaxProfile>[] = [
    {
      header: 'NCM',
      accessor: 'ncmCode' as const,
      render: (v: string | null) => v ?? '-',
    },
    {
      header: 'Produto',
      accessor: 'productId' as const,
      render: (v: string | null) => v ? (
        <span className="cell-id" title={v}>{v.slice(0, 8)}...</span>
      ) : '-',
    },
    {
      header: 'Classificação Tributária',
      accessor: 'classificacaoTributaria' as const,
      render: (v: string | null) => v ? <Badge variant="info">{v}</Badge> : '-',
    },
    {
      header: 'Redução IBS',
      accessor: 'reducaoIbs' as const,
      render: (v: number | null) => formatPercent(v),
    },
    {
      header: 'Redução CBS',
      accessor: 'reducaoCbs' as const,
      render: (v: number | null) => formatPercent(v),
    },
    {
      header: 'CST IBS/CBS',
      accessor: 'cstIbsCbs' as const,
      render: (v: string | null) => v ?? '-',
    },
    {
      header: 'Monofásico',
      accessor: 'monofasico' as const,
      render: (v: boolean) => yesNo(v),
    },
    {
      header: 'Diferimento',
      accessor: 'diferimento' as const,
      render: (v: boolean) => yesNo(v),
    },
    {
      header: 'Ações',
      accessor: 'id' as const,
      render: (_v: string, row: TaxProfile) => (
        <div className="cell-actions">
          <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
            Editar
          </Button>
          <Button variant="danger" size="sm" onClick={() => setDeleteTarget(row)}>
            Excluir
          </Button>
        </div>
      ),
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="tax-profiles-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Perfis Fiscais</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Perfis fiscais aplicados a produtos e operações.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={openCreate}>
            Novo Perfil
          </Button>
        </div>
      </div>

      <Card>
        {error ? (
          <div style={{ padding: '3rem 2rem', textAlign: 'center' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem', color: '#ba1a1a' }}>
              Erro ao carregar perfis fiscais
            </p>
            <p style={{ fontSize: '0.875rem', margin: '0 0 1rem', color: '#74777f' }}>
              {(error as Error).message}
            </p>
            <Button variant="primary" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <Table
            columns={columns}
            data={profiles ?? []}
            isLoading={isLoading}
            emptyMessage="Nenhum perfil fiscal encontrado"
          />
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Editar Perfil Fiscal' : 'Novo Perfil Fiscal'}
        size="md"
      >
        <div className="modal-form">
          <Input
            label="Prefixo NCM"
            value={form.ncmCode ?? ''}
            onChange={(e) => setForm({ ...form, ncmCode: e.target.value || null })}
            placeholder="Ex.: 8471.30"
          />
          <Input
            label="ID do Produto"
            value={form.productId ?? ''}
            onChange={(e) => setForm({ ...form, productId: e.target.value || null })}
            placeholder="UUID do produto (opcional)"
          />

          {/* Classificação Tributária */}
          <div className="form-group">
            <label>Classificação Tributária</label>
            <select
              className="form-select"
              value={form.classificacaoTributaria ?? ''}
              onChange={(e) =>
                setForm({ ...form, classificacaoTributaria: e.target.value || null })
              }
            >
              <option value="">Selecione...</option>
              {CLASSIFICACAO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <Input
              label="Redução IBS (%)"
              type="number"
              value={form.reducaoIbs != null ? String(form.reducaoIbs) : ''}
              onChange={(e) =>
                setForm({ ...form, reducaoIbs: e.target.value ? Number(e.target.value) : null })
              }
              placeholder="0"
            />
            <Input
              label="Redução CBS (%)"
              type="number"
              value={form.reducaoCbs != null ? String(form.reducaoCbs) : ''}
              onChange={(e) =>
                setForm({ ...form, reducaoCbs: e.target.value ? Number(e.target.value) : null })
              }
              placeholder="0"
            />
          </div>

          <Input
            label="CST IBS/CBS"
            value={form.cstIbsCbs ?? ''}
            onChange={(e) => setForm({ ...form, cstIbsCbs: e.target.value || null })}
            placeholder="Ex.: 00, 20, 60"
          />

          <div className="form-group">
            <label>Monofásico</label>
            <Toggle
              checked={form.monofasico ?? false}
              onChange={(checked) => setForm({ ...form, monofasico: checked })}
            />
          </div>

          <div className="form-group">
            <label>Diferimento</label>
            <Toggle
              checked={form.diferimento ?? false}
              onChange={(checked) => setForm({ ...form, diferimento: checked })}
            />
          </div>

          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>

          {(createMutation.isError || updateMutation.isError) && (
            <p className="error-message">
              {((createMutation.error ?? updateMutation.error) as Error).message}
            </p>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Confirmar Exclusão"
      >
        <p>
          Tem certeza que deseja excluir este perfil fiscal?
          {deleteTarget?.ncmCode && (
            <> (NCM: <strong>{deleteTarget.ncmCode}</strong>)</>
          )}
          Esta ação não pode ser desfeita.
        </p>
        <div className="modal-actions">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteTarget && handleDelete(deleteTarget)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
        {deleteMutation.isError && (
          <p className="error-message">{(deleteMutation.error as Error).message}</p>
        )}
      </Modal>

      <style>{`
        .tax-profiles-page { padding: 1.5rem; }
        .cell-id { font-family: monospace; font-size: 0.8rem; }
        .cell-actions { display: flex; gap: 0.25rem; }
        .modal-form { display: flex; flex-direction: column; gap: 1rem; }
        .form-group { display: flex; flex-direction: column; gap: 0.25rem; }
        .form-group label { font-size: 0.875rem; color: #43474e; font-weight: 500; }
        .form-select { padding: 0.5rem; border: 1px solid #c4c6cf; border-radius: 4px; font-size: 0.875rem; height: 40px; width: 100%; background: #ffffff; }
        .form-row { display: flex; gap: 1rem; }
        .form-row > * { flex: 1; }
        .modal-actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.5rem; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
      `}</style>
    </div>
  );
}
